import { z } from 'zod';
import { gridLedStandbyPlanningInputSchema, type GridLedStandbyPlanningSnapshot,
  type GenerationAssessmentSelection } from '../../types/gridLedStandbyPlanning';
import { solveGridLedStandbyPlanning } from './gridLedStandbyPlanning';
import { hashGenerationImpactValue } from './integrity';

const id = z.string().min(1).max(160).regex(/^[A-Za-z0-9_.:-]+$/);
const sha = z.string().regex(/^[a-f\d]{64}$/);
const timestamp = z.string().datetime();
const snapshotSchema = z.object({
  schemaVersion: z.literal(1), id, name: z.string().min(1).max(140), siteId: id, ownerUid: id,
  input: gridLedStandbyPlanningInputSchema, result: z.unknown(), inputHash: sha, resultHash: sha,
  createdAt: timestamp, updatedAt: timestamp, createdBy: id, promotedAt: timestamp.optional(),
}).strict();

export const generationAssessmentSelectionSchema = z.object({
  schemaVersion: z.literal(1), kind: z.enum(['grid-led-standby-planning', 'generation-impact-v1']),
  assessmentId: id, inputHash: sha, resultHash: sha, selectedAt: timestamp,
}).strict();

/** Historical read/replay is independent of mutable current attachments. */
export function requireGridLedStandbyPlanningIntegrity(candidate: unknown): GridLedStandbyPlanningSnapshot {
  const snapshot = snapshotSchema.parse(candidate);
  if (snapshot.siteId !== snapshot.input.siteId || snapshot.ownerUid !== snapshot.input.ownerUid
    || snapshot.createdBy !== snapshot.ownerUid) throw new Error('Planning snapshot identity differs from the frozen input.');
  if (hashGenerationImpactValue(snapshot.input) !== snapshot.inputHash
    || hashGenerationImpactValue(snapshot.result) !== snapshot.resultHash) throw new Error('Planning snapshot input or result hash mismatch.');
  const computed = solveGridLedStandbyPlanning(snapshot.input);
  if (hashGenerationImpactValue(computed) !== snapshot.resultHash) throw new Error('Planning saved result differs from the versioned accounting model.');
  return { ...snapshot, result: computed };
}

/** Explicit planning selection never falls back to stale v1 authority. */
export function resolveSelectedGridLedStandbyPlanning(
  selection: GenerationAssessmentSelection | undefined,
  candidate: unknown,
): GridLedStandbyPlanningSnapshot | null {
  if (selection === undefined) return null;
  const pointer = generationAssessmentSelectionSchema.parse(selection);
  if (pointer.kind !== 'grid-led-standby-planning') return null;
  const snapshot = requireGridLedStandbyPlanningIntegrity(candidate);
  if (snapshot.id !== pointer.assessmentId || snapshot.inputHash !== pointer.inputHash || snapshot.resultHash !== pointer.resultHash
    || snapshot.promotedAt !== pointer.selectedAt) throw new Error('Selected planning assessment differs from its exact promoted revision.');
  return snapshot;
}
